import { useState } from 'react'
import {
  Activity,
  CircleAlert,
  Clock,
  Hash,
  KeyRound,
  KeySquare,
  Trash2,
  TriangleAlert
} from 'lucide-react'
import type { SshKeyDTO } from '@shared/types'
import { api, errorMessage, isApiError } from '@/lib/api'
import { formatNumber, pluralize, relativeTime } from '@/lib/format'
import { Badge, Button, Card, CardBody, CardFooter, Modal, useToast } from '@/components/ui'

export interface KeyCardProps {
  sshKey: SshKeyDTO
  onDeleted: (id: string) => void
  /** Refetch the list: designating one key un-designates whatever held it before. */
  onDesignationChanged: () => void
}

export default function KeyCard({
  sshKey,
  onDeleted,
  onDesignationChanged
}: KeyCardProps): React.ReactElement {
  const { toast } = useToast()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [designating, setDesignating] = useState(false)
  // Kept in the dialog: a 409 explains what to fix, and the fix is right here.
  const [failure, setFailure] = useState<string | null>(null)

  const inUse = sshKey.siteCount > 0

  const handleDesignate = async (share: boolean): Promise<void> => {
    setDesignating(true)
    try {
      if (share) await api.post<SshKeyDTO>(`/ssh-keys/${sshKey.id}/designate`, {})
      else await api.del<void>('/ssh-keys/instance')
      toast({
        title: share ? 'Key shared with everyone' : 'Key no longer shared',
        description: share
          ? `All users now connect WP Engine sites using ${sshKey.label}.`
          : `${sshKey.label} is no longer the instance key. New site connections will fail until another key is shared.`,
        variant: share ? 'success' : 'info'
      })
      // The whole list changes, not just this card — refetch rather than patch.
      onDesignationChanged()
    } catch (cause) {
      toast({ title: 'Could not update', description: errorMessage(cause), variant: 'error' })
    } finally {
      setDesignating(false)
    }
  }

  const handleDelete = async (): Promise<void> => {
    setDeleting(true)
    setFailure(null)
    try {
      await api.del<void>(`/ssh-keys/${sshKey.id}`)
      toast({
        title: 'SSH key deleted',
        description: `${sshKey.label} was removed.`,
        variant: 'success'
      })
      setConfirmOpen(false)
      onDeleted(sshKey.id)
    } catch (cause) {
      setDeleting(false)
      setFailure(
        isApiError(cause) && cause.status === 409
          ? cause.message
          : errorMessage(cause)
      )
    }
  }

  return (
    <Card className="flex w-full flex-col">
      <CardBody className="flex-1">
        <div className="flex items-start gap-3">
          <span
            aria-hidden="true"
            className="grid size-10 shrink-0 place-items-center rounded-control bg-accent-soft text-accent"
          >
            <KeySquare className="size-5" />
          </span>

          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <h3 className="min-w-0 truncate text-[15px] leading-tight font-semibold text-fg">
                {sshKey.label}
              </h3>
              <Badge size="sm" variant="accent">
                {sshKey.keyType}
              </Badge>
              <Badge size="sm" variant={inUse ? 'info' : 'neutral'}>
                {formatNumber(sshKey.siteCount)} {pluralize(sshKey.siteCount, 'site')}
              </Badge>
              {sshKey.isInstanceKey ? (
                <Badge size="sm" variant="success">
                  In use by everyone
                </Badge>
              ) : null}
            </div>

            <div className="mt-1.5 flex min-w-0 items-center gap-1.5">
              <Hash className="size-3.5 shrink-0 text-fg-subtle" aria-hidden="true" />
              <code
                title={sshKey.fingerprint}
                className="min-w-0 truncate font-mono text-xs text-fg-muted"
              >
                {sshKey.fingerprint}
              </code>
            </div>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-fg-muted">
          <span className="inline-flex items-center gap-1.5">
            <Clock className="size-3.5 shrink-0 text-fg-subtle" aria-hidden="true" />
            Added {relativeTime(sshKey.createdAt)}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Activity className="size-3.5 shrink-0 text-fg-subtle" aria-hidden="true" />
            {sshKey.lastUsedAt ? `Last used ${relativeTime(sshKey.lastUsedAt)}` : 'Never used'}
          </span>
        </div>
      </CardBody>

      <CardFooter className="justify-between gap-2">
        {/* Designating is what makes a key usable by everyone, so it sits on the left
            as the primary action; deleting stays on the right, away from it. */}
        {sshKey.isInstanceKey ? (
          <Button
            variant="secondary"
            size="sm"
            iconLeft={<KeyRound />}
            loading={designating}
            onClick={() => void handleDesignate(false)}
          >
            Stop sharing this key
          </Button>
        ) : (
          <Button
            variant="secondary"
            size="sm"
            iconLeft={<KeyRound />}
            loading={designating}
            onClick={() => void handleDesignate(true)}
          >
            Use for everyone
          </Button>
        )}
        <Button
          variant="danger"
          size="sm"
          iconLeft={<Trash2 />}
          onClick={() => {
            setFailure(null)
            setConfirmOpen(true)
          }}
        >
          Delete
        </Button>
      </CardFooter>

      <Modal
        open={confirmOpen}
        onClose={() => {
          if (!deleting) setConfirmOpen(false)
        }}
        title="Delete SSH key"
        size="sm"
        footer={
          <>
            <Button variant="secondary" disabled={deleting} onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              iconLeft={<Trash2 />}
              loading={deleting}
              onClick={() => void handleDelete()}
            >
              Delete key
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm leading-relaxed text-fg">
            Delete <span className="font-semibold">{sshKey.label}</span>? Argus discards its
            encrypted copy of the private key — this cannot be undone, though the key itself still
            exists on your machine and in the WP Engine portal.
          </p>

          {inUse ? (
            <div className="flex gap-2.5 rounded-control border border-warn-500/40 bg-warn-100/60 px-3 py-2.5 dark:bg-warn-950/50">
              <TriangleAlert
                className="mt-0.5 size-4 shrink-0 text-warn-700 dark:text-warn-300"
                aria-hidden="true"
              />
              <p className="text-sm leading-relaxed text-fg-muted">
                {formatNumber(sshKey.siteCount)} {pluralize(sshKey.siteCount, 'site')} still{' '}
                {pluralize(sshKey.siteCount, 'uses', 'use')} this key. Point{' '}
                {pluralize(sshKey.siteCount, 'it', 'them')} at another key first — Argus refuses to
                delete a key that is still in use.
              </p>
            </div>
          ) : null}

          {failure ? (
            <div
              role="alert"
              className="flex gap-2.5 rounded-control border border-bad-500/40 bg-bad-100/60 px-3 py-2.5 dark:bg-bad-950/50"
            >
              <CircleAlert
                className="mt-0.5 size-4 shrink-0 text-bad-700 dark:text-bad-300"
                aria-hidden="true"
              />
              <div className="min-w-0 space-y-1 text-sm leading-relaxed">
                <p className="font-semibold text-bad-700 dark:text-bad-300">Key not deleted</p>
                <p className="break-words text-fg-muted">{failure}</p>
              </div>
            </div>
          ) : null}
        </div>
      </Modal>
    </Card>
  )
}

export { KeyCard }
